// Sección inline de "Mis Tareas Pendientes" para embeber en el Perfil.
// Reutiliza la misma lógica que MisTareasPanel pero sin Drawer.

import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, IconButton, Chip, Button, CircularProgress, Paper, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Table, TableHead, TableRow, TableCell, TableBody,
    Tabs, Tab, Checkbox, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import AdjuntosSolicitud from '../../components/AdjuntosSolicitud';
import { nodeApiPath } from '../../utils/paths';
import { triggerSessionExpired } from '../../api/endpoints';

const BR = '4px';
const fmtDate = (d) => { if (!d) return '-'; return new Date(d).toLocaleDateString('es-AR'); };

async function jf(path, options = {}) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('No hay token'); }
    const res = await fetch(nodeApiPath(path), {
        ...options,
        headers: {
            'Accept': 'application/json',
            ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

const rolColors = {
    DIRECTOR: { bg: '#e3f2fd', color: '#1565c0', label: 'Como Director' },
    DIR_ADMIN: { bg: '#fff3e0', color: '#e65100', label: 'Como Dir. Administrativa' },
    JURIDICO: { bg: '#fffde7', color: '#f57f17', label: 'Como Jurídico' },
    AUTORIDAD: { bg: '#f3e5f5', color: '#6a1b9a', label: 'Como Autoridad' },
};

const rolLabelAvanzar = {
    DIRECTOR: 'Aprobar',
    AUTORIDAD: 'Finalizar',
    DIR_ADMIN: 'Dar visto bueno',
    JURIDICO: 'Enviar dictamen',
};

function ResumenInline({ idPersonal }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!idPersonal || !open || data) return;
        jf(`personal/resumen-ano/${idPersonal}`).then(r => { if (r.ok) setData(r); }).catch(() => { });
    }, [idPersonal, open, data]);
    if (!idPersonal) return null;
    const totLic = data ? data.licencias.reduce((acc, l) => acc + l.Dias, 0) : 0;
    const totTard = data?.asistencia?.Tardanzas || 0;
    const totAus = data?.asistencia?.Ausencias || 0;
    const horas = Math.floor((data?.asistencia?.MinTardanza || 0) / 60);
    const min = (data?.asistencia?.MinTardanza || 0) % 60;
    return (
        <Box sx={{ mt: 1 }}>
            <Button size="small" onClick={() => setOpen(o => !o)} sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#666', minHeight: 0, py: 0 }}>
                {open ? '▼' : '▶'} Ver antecedentes del año
            </Button>
            {open && (
                <Box sx={{ mt: 0.5, p: 1, bgcolor: '#fafafa', borderRadius: BR, border: '1px solid #e0e0e0' }}>
                    {!data ? <Typography variant="caption" sx={{ color: '#999' }}>Cargando...</Typography> : (
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <Chip size="small" label={`${totLic} días de licencia`} sx={{ bgcolor: '#e0f2f1', color: '#00695c', fontSize: '0.65rem' }} />
                            <Chip size="small" label={`${totTard} tardanzas (${horas}h ${min}m)`} sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.65rem' }} />
                            <Chip size="small" label={`${totAus} ausencias`} sx={{ bgcolor: '#ffebee', color: '#c62828', fontSize: '0.65rem' }} />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default function MisTareasSeccion({ notify, onCountChange }) {
    const [tab, setTab] = useState('pendientes');
    const [items, setItems] = useState([]);
    const [historial, setHistorial] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingHist, setLoadingHist] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rechazarDlg, setRechazarDlg] = useState({ open: false, id: null, motivo: '' });
    const [detalleDlg, setDetalleDlg] = useState({ open: false, sol: null, trail: [] });
    const [adjDlg, setAdjDlg] = useState({ open: false, solicitud: null });
    const [seleccionados, setSeleccionados] = useState(new Set());
    const [masivoDlg, setMasivoDlg] = useState({ open: false, tipo: null, motivo: '' });

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await jf('personal/bandeja/mis-tareas');
            const arr = r.ok ? (r.items || []) : [];
            setItems(arr);
            onCountChange?.(arr.length);
        } catch { setItems([]); onCountChange?.(0); }
        setLoading(false);
    }, [onCountChange]);

    const cargarHistorial = useCallback(async () => {
        setLoadingHist(true);
        try {
            const r = await jf('personal/bandeja/mis-tareas-historial?limit=100');
            setHistorial(r.ok ? (r.items || []) : []);
        } catch { setHistorial([]); }
        setLoadingHist(false);
    }, []);

    useEffect(() => { cargar(); }, [cargar]);
    useEffect(() => { if (tab === 'historial') cargarHistorial(); }, [tab, cargarHistorial]);

    const avanzar = async (id) => {
        setSaving(true);
        try {
            const r = await jf(`personal/solicitudes/${id}/avanzar`, { method: 'POST', body: JSON.stringify({}) });
            if (r.ok) { notify?.(r.disposicion ? `OK — Disposición ${r.disposicion}` : 'Solicitud aprobada', 'success'); cargar(); }
            else notify?.(r.mensaje || 'Error', 'error');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setSaving(false);
    };

    const rechazar = async () => {
        if (!rechazarDlg.motivo.trim()) { notify?.('Indicá un motivo', 'warning'); return; }
        setSaving(true);
        try {
            const r = await jf(`personal/solicitudes/${rechazarDlg.id}/rechazar-v2`, {
                method: 'POST', body: JSON.stringify({ motivo: rechazarDlg.motivo }),
            });
            if (r.ok) { notify?.('Rechazada', 'info'); setRechazarDlg({ open: false, id: null, motivo: '' }); cargar(); }
            else notify?.(r.mensaje || 'Error', 'error');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setSaving(false);
    };

    const toggleSel = (id) => {
        setSeleccionados(prev => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id); else s.add(id);
            return s;
        });
    };
    const toggleAllRol = (lista) => {
        setSeleccionados(prev => {
            const s = new Set(prev);
            const todosYaSeleccionados = lista.every(x => s.has(x.Id));
            lista.forEach(x => { if (todosYaSeleccionados) s.delete(x.Id); else s.add(x.Id); });
            return s;
        });
    };
    const confirmarMasivo = async () => {
        const ids = Array.from(seleccionados);
        if (ids.length === 0) return;
        setSaving(true);
        try {
            const endpoint = masivoDlg.tipo === 'aprobar' ? 'personal/solicitudes/avanzar-masivo' : 'personal/solicitudes/rechazar-masivo';
            const body = masivoDlg.tipo === 'aprobar' ? { ids, observacion: masivoDlg.motivo } : { ids, motivo: masivoDlg.motivo };
            const r = await jf(endpoint, { method: 'POST', body: JSON.stringify(body) });
            if (r.ok) {
                notify?.(`${masivoDlg.tipo === 'aprobar' ? 'Aprobadas' : 'Rechazadas'}: ${r.exitosas}${r.fallidas > 0 ? ` · Fallaron ${r.fallidas}` : ''}`, r.fallidas > 0 ? 'warning' : 'success');
                setSeleccionados(new Set());
                setMasivoDlg({ open: false, tipo: null, motivo: '' });
                cargar();
            } else notify?.(r.mensaje || 'Error', 'error');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setSaving(false);
    };

    const abrirDetalle = async (sol) => {
        try {
            const r = await jf(`personal/solicitudes/${sol.Id}/trail`);
            setDetalleDlg({ open: true, sol, trail: r.ok ? (r.trail || []) : [] });
        } catch { setDetalleDlg({ open: true, sol, trail: [] }); }
    };

    const porRol = items.reduce((acc, it) => { (acc[it.Mi_Rol] = acc[it.Mi_Rol] || []).push(it); return acc; }, {});

    return (
        <Box>
            <Paper sx={{ borderRadius: BR, mb: 2, overflow: 'hidden' }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}
                    sx={{ borderBottom: '1px solid #e0e0e0', '& .MuiTab-root': { textTransform: 'none', fontSize: '0.85rem' } }}>
                    <Tab value="pendientes" label={`Pendientes${items.length > 0 ? ` (${items.length})` : ''}`} />
                    <Tab value="historial" label={`Historial${historial.length > 0 ? ` (${historial.length})` : ''}`} />
                </Tabs>
            </Paper>

            {tab === 'historial' && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Button size="small" startIcon={<RefreshIcon />} onClick={cargarHistorial} sx={{ textTransform: 'none' }}>Refrescar</Button>
                    </Box>
                    {loadingHist ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
                    ) : historial.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#999', py: 4, textAlign: 'center' }}>Todavía no aprobaste ni rechazaste ninguna solicitud.</Typography>
                    ) : (
                        <Box>
                            {historial.map(h => {
                                const accionColor = h.Accion === 'APROBAR' || h.Accion === 'FINALIZAR' ? '#2e7d32'
                                    : h.Accion === 'RECHAZAR' ? '#c62828' : '#1565c0';
                                const accionLabel = h.Accion === 'APROBAR' ? 'Aprobaste' : h.Accion === 'FINALIZAR' ? 'Finalizaste'
                                    : h.Accion === 'RECHAZAR' ? 'Rechazaste' : 'Derivaste';
                                return (
                                    <Paper key={h.Id_Trail} sx={{ p: 1.5, mb: 1, borderRadius: BR, borderLeft: `4px solid ${accionColor}` }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    <span style={{ color: accionColor }}>{accionLabel}</span>
                                                    {' '}solicitud de {h.Apellido}, {h.Nombre}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#666' }}>
                                                    Como <strong>{h.Rol || '—'}</strong> · {new Date(h.Fecha).toLocaleString('es-AR')}
                                                </Typography>
                                            </Box>
                                            <Chip label={h.Tipo} size="small" sx={{
                                                bgcolor: h.Tipo === 'VACACIONES' ? '#e0f2f1' : h.Tipo === 'ART42' ? '#f3e5f5' : h.Tipo === 'CARTA_MEDICA' ? '#fff3e0' : '#e3f2fd',
                                                color: h.Tipo === 'VACACIONES' ? '#00695c' : h.Tipo === 'ART42' ? '#6a1b9a' : h.Tipo === 'CARTA_MEDICA' ? '#bf360c' : '#1565c0',
                                                fontSize: '0.65rem',
                                            }} />
                                        </Box>
                                        <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>
                                            <strong>{fmtDate(h.Fecha_Desde)}</strong> a <strong>{fmtDate(h.Fecha_Hasta)}</strong> — {h.Dias_Habiles} días hábiles
                                            {' · '}Estado actual: <strong>{h.Estado_Actual}</strong>
                                        </Typography>
                                        {h.Observacion && (
                                            <Typography variant="caption" sx={{ display: 'block', color: '#666', fontStyle: 'italic', mt: 0.5 }}>
                                                "{h.Observacion}"
                                            </Typography>
                                        )}
                                        <Box sx={{ mt: 0.5 }}>
                                            <Tooltip title="Ver historial completo">
                                                <IconButton size="small" onClick={() => abrirDetalle({ Id: h.Id_Solicitud, Apellido: h.Apellido, Nombre: h.Nombre })}>
                                                    <HistoryIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}

            {tab === 'pendientes' && loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
            {tab === 'pendientes' && !loading && items.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                    <Typography variant="body2">No tenés tareas pendientes de aprobar.</Typography>
                </Box>
            )}

            {tab === 'pendientes' && !loading && items.length > 0 && (
                <Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        {seleccionados.size > 0 ? (
                            <>
                                <Chip label={`${seleccionados.size} seleccionadas`} color="primary" size="small" sx={{ fontWeight: 600 }} />
                                <Button size="small" variant="contained" startIcon={<CheckCircleIcon />}
                                    onClick={() => setMasivoDlg({ open: true, tipo: 'aprobar', motivo: '' })} disabled={saving}
                                    sx={{ bgcolor: '#2e7d32', textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#1b5e20' } }}>
                                    Aprobar seleccionadas
                                </Button>
                                <Button size="small" variant="outlined" startIcon={<CancelIcon />}
                                    onClick={() => setMasivoDlg({ open: true, tipo: 'rechazar', motivo: '' })} disabled={saving}
                                    sx={{ borderColor: '#c62828', color: '#c62828', textTransform: 'none', borderRadius: BR }}>
                                    Rechazar seleccionadas
                                </Button>
                                <Button size="small" onClick={() => setSeleccionados(new Set())} sx={{ textTransform: 'none', color: '#666' }}>
                                    Cancelar
                                </Button>
                            </>
                        ) : (
                            <Typography variant="caption" sx={{ color: '#666' }}>
                                Tip: marcá las casillas para aprobar o rechazar varias solicitudes a la vez.
                            </Typography>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" startIcon={<RefreshIcon />} onClick={cargar} sx={{ textTransform: 'none' }}>Refrescar</Button>
                    </Box>

                    {Object.entries(porRol).map(([rol, lista]) => {
                const rc = rolColors[rol] || { bg: '#f5f5f5', color: '#333', label: rol };
                return (
                    <Box key={rol} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Checkbox size="small"
                                checked={lista.length > 0 && lista.every(x => seleccionados.has(x.Id))}
                                indeterminate={lista.some(x => seleccionados.has(x.Id)) && !lista.every(x => seleccionados.has(x.Id))}
                                onChange={() => toggleAllRol(lista)} sx={{ p: 0.5 }} />
                            <Chip label={rc.label} sx={{ bgcolor: rc.bg, color: rc.color, fontWeight: 600 }} size="small" />
                            <Typography variant="caption" sx={{ color: '#666' }}>{lista.length} solicitud{lista.length !== 1 ? 'es' : ''}</Typography>
                        </Box>
                        {lista.map(s => (
                            <Paper key={s.Id} sx={{ p: 1.5, mb: 1, borderRadius: BR, borderLeft: `4px solid ${rc.color}`,
                                bgcolor: seleccionados.has(s.Id) ? rc.bg : '#fff' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                        <Checkbox size="small" checked={seleccionados.has(s.Id)} onChange={() => toggleSel(s.Id)} sx={{ p: 0.5 }} />
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.Apellido}, {s.Nombre}</Typography>
                                            <Typography variant="caption" sx={{ color: '#666' }}>{s.Area_Empleado || '—'}</Typography>
                                        </Box>
                                    </Box>
                                    <Chip label={s.Tipo} size="small" sx={{
                                        bgcolor: s.Tipo === 'VACACIONES' ? '#e0f2f1' : s.Tipo === 'ART42' ? '#f3e5f5' : s.Tipo === 'CARTA_MEDICA' ? '#fff3e0' : '#e3f2fd',
                                        color: s.Tipo === 'VACACIONES' ? '#00695c' : s.Tipo === 'ART42' ? '#6a1b9a' : s.Tipo === 'CARTA_MEDICA' ? '#bf360c' : '#1565c0',
                                        fontSize: '0.65rem',
                                    }} />
                                </Box>
                                <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>
                                    <strong>{fmtDate(s.Fecha_Desde)}</strong> a <strong>{fmtDate(s.Fecha_Hasta)}</strong> — {s.Dias_Habiles} días hábiles
                                </Typography>
                                {s.Articulo && (
                                    <Typography variant="caption" sx={{ display: 'block', color: '#666', mt: 0.5, fontSize: '0.7rem' }}>
                                        {(s.Articulo || '').replace(/[\r\n\t]+/g, ' ').substring(0, 120)}{s.Articulo?.length > 120 ? '…' : ''}
                                    </Typography>
                                )}
                                {s.Observaciones && (
                                    <Typography variant="caption" sx={{ display: 'block', color: '#666', mt: 0.5, fontStyle: 'italic' }}>
                                        "{s.Observaciones}"
                                    </Typography>
                                )}
                                {rol === 'AUTORIDAD' && s.Disp_Numero && (
                                    <Typography variant="caption" sx={{ display: 'block', color: rc.color, fontWeight: 600, mt: 0.5 }}>
                                        Disposición {String(s.Disp_Numero).padStart(3, '0')}-SG-{s.Disp_Ano} (BORRADOR)
                                    </Typography>
                                )}
                                <ResumenInline idPersonal={s.Id_Personal} />
                                {rol === 'JURIDICO' && (
                                    <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: BR, border: '1px solid #ffe0b2' }}>
                                        <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                            Adjuntá el dictamen antes de avanzar:
                                        </Typography>
                                        <Button size="small" variant="outlined" startIcon={<AttachFileIcon />}
                                            onClick={() => setAdjDlg({ open: true, solicitud: s })}
                                            sx={{ textTransform: 'none', borderColor: '#e65100', color: '#e65100', borderRadius: BR }}>
                                            Subir dictamen
                                        </Button>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                                    <Button size="small" variant="contained" startIcon={<CheckCircleIcon />} onClick={() => avanzar(s.Id)} disabled={saving}
                                        sx={{ bgcolor: rc.color, textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: rc.color, filter: 'brightness(0.9)' } }}>
                                        {rolLabelAvanzar[rol] || 'Avanzar'}
                                    </Button>
                                    <Button size="small" variant="outlined" startIcon={<CancelIcon />}
                                        onClick={() => setRechazarDlg({ open: true, id: s.Id, motivo: '' })} disabled={saving}
                                        sx={{ borderColor: '#c62828', color: '#c62828', textTransform: 'none', borderRadius: BR }}>
                                        Rechazar
                                    </Button>
                                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25 }}>
                                        {(s.Tipo === 'LICENCIA' || s.Tipo === 'CARTA_MEDICA') && (
                                            <Tooltip title="Ver adjuntos">
                                                <IconButton size="small" onClick={() => setAdjDlg({ open: true, solicitud: s })} sx={{ color: '#1565c0' }}>
                                                    <AttachFileIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {s.Id_Disposicion && (
                                            <Tooltip title="Ver escrito">
                                                <IconButton size="small" onClick={async () => {
                                                    try {
                                                        const r = await jf(`personal/disposiciones/${s.Id_Disposicion}`);
                                                        if (r.ok && r.disposicion?.Contenido_HTML) {
                                                            const w = window.open('', '_blank');
                                                            w.document.write(`<html><head><title>${r.disposicion.Formato}</title><style>body{font-family:Times,serif;max-width:800px;margin:30px auto;padding:20px}</style></head><body>${r.disposicion.Contenido_HTML}</body></html>`);
                                                            w.document.close();
                                                        }
                                                    } catch { }
                                                }} sx={{ color: '#6a1b9a' }}>
                                                    <DescriptionIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Historial">
                                            <IconButton size="small" onClick={() => abrirDetalle(s)}>
                                                <HistoryIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                );
            })}
                </Box>
            )}

            <AdjuntosSolicitud
                open={adjDlg.open} idSolicitud={adjDlg.solicitud?.Id} solicitud={adjDlg.solicitud}
                onClose={() => setAdjDlg({ open: false, solicitud: null })}
                puedeSubir={true} rolUsuario={adjDlg.solicitud?.Mi_Rol || 'EMPLEADO'} notify={notify}
            />

            {/* Dialog acción masiva */}
            <Dialog open={masivoDlg.open} onClose={() => setMasivoDlg({ open: false, tipo: null, motivo: '' })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: masivoDlg.tipo === 'aprobar' ? '#e8f5e9' : '#ffebee', color: masivoDlg.tipo === 'aprobar' ? '#2e7d32' : '#c62828', fontWeight: 700 }}>
                    {masivoDlg.tipo === 'aprobar' ? 'Aprobar ' : 'Rechazar '}{seleccionados.size} solicitud{seleccionados.size !== 1 ? 'es' : ''}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2, borderRadius: BR, fontSize: '0.8rem' }}>
                        La acción se aplicará a cada una individualmente. Si alguna falla (por ejemplo, cambió de estado), las demás seguirán.
                    </Alert>
                    <TextField label={masivoDlg.tipo === 'aprobar' ? 'Observación (opcional)' : 'Motivo de rechazo'}
                        multiline rows={3} fullWidth required={masivoDlg.tipo === 'rechazar'}
                        value={masivoDlg.motivo}
                        onChange={e => setMasivoDlg(p => ({ ...p, motivo: e.target.value }))}
                        sx={{ mt: 1 }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMasivoDlg({ open: false, tipo: null, motivo: '' })} sx={{ textTransform: 'none' }} disabled={saving}>Cancelar</Button>
                    <Button onClick={confirmarMasivo} variant="contained"
                        disabled={saving || (masivoDlg.tipo === 'rechazar' && !masivoDlg.motivo.trim())}
                        sx={{ bgcolor: masivoDlg.tipo === 'aprobar' ? '#2e7d32' : '#c62828', textTransform: 'none', borderRadius: BR, '&:hover': { filter: 'brightness(0.9)' } }}>
                        {saving ? 'Procesando...' : `Confirmar ${masivoDlg.tipo === 'aprobar' ? 'aprobación' : 'rechazo'}`}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={rechazarDlg.open} onClose={() => setRechazarDlg({ open: false, id: null, motivo: '' })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 }}>Rechazar solicitud</DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <TextField label="Motivo" multiline rows={4} fullWidth required value={rechazarDlg.motivo}
                        onChange={e => setRechazarDlg(p => ({ ...p, motivo: e.target.value }))} sx={{ mt: 1 }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRechazarDlg({ open: false, id: null, motivo: '' })} sx={{ textTransform: 'none' }} disabled={saving}>Cancelar</Button>
                    <Button onClick={rechazar} variant="contained" color="error" disabled={saving || !rechazarDlg.motivo.trim()} sx={{ textTransform: 'none', borderRadius: BR }}>
                        {saving ? 'Rechazando...' : 'Rechazar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={detalleDlg.open} onClose={() => setDetalleDlg({ open: false, sol: null, trail: [] })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#1769aa', color: '#fff', fontWeight: 700 }}>
                    Historial — {detalleDlg.sol?.Apellido}, {detalleDlg.sol?.Nombre}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {detalleDlg.trail.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#999', py: 2, textAlign: 'center' }}>Sin eventos</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead><TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Acción</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Rol</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Estado</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Usuario</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Obs.</TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {detalleDlg.trail.map(t => (
                                    <TableRow key={t.Id}>
                                        <TableCell sx={{ fontSize: '0.72rem' }}>{new Date(t.Fecha).toLocaleString('es-AR')}</TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem' }}>{t.Accion}</TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem' }}>{t.Rol || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem', color: '#666' }}>{t.Estado_Anterior ? `${t.Estado_Anterior} → ` : ''}<strong>{t.Estado_Nuevo}</strong></TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem' }}>{t.Usuario || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem', color: '#666' }}>{t.Observacion || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetalleDlg({ open: false, sol: null, trail: [] })} sx={{ textTransform: 'none' }}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
